import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import API from "../../utils/api";
import Loader from "../Shared/Loader";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await API.post("/auth/login", { email, password });
      const token = res.data.token;

      const me = await API.get("/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = me.data.data;
      login(user, token);

      if (user.role === "super_admin") {
        navigate("/admin/companies-list");
      } else if (user.role === "admin") {
        navigate("/company/companies-list");
      } else if (user.role === "viewer") {
        navigate("/viewer/companies-list");
      } else {
        navigate("/not-found");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col w-full md:flex-row md:w-2/3  bg-white">
      {/* Left Panel - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={handleLogin} className="w-full max-w-md space-y-5">
          <h2 className="text-3xl font-bold text-gray-900 text-center">
            Welcome Back
          </h2>
          <p className="text-sm text-gray-500 text-center">
            Login to manage your company profile
          </p>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <input
            type="email"
            placeholder="Email"
            className="w-full p-3 border border-gray-300 rounded outline-none focus:ring-0"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <div className="relative w-full">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="w-full p-3 pr-12 border border-gray-200 rounded outline-none focus:ring-0"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-gray-900 text-white font-bold rounded hover:bg-gray-700 transition"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          <p className="text-center text-sm text-gray-600">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="text-gray-900 text-md font-bold hover:underline"
            >
              Register here
            </Link>
          </p>
        </form>
      </div>

      {/* Right Panel - Welcome Content */}
      <div className="hidden md:flex w-full md:w-1/2 bg-gray-900 text-white items-center justify-center p-10">
        <div className="max-w-sm">
          <h2 className="text-3xl font-bold mb-4">Manage Your Company</h2>
          <p className="text-gray-300 mb-6">
            Login to update your listings, view insights, and keep your business
            profile up to date.
          </p>

          <div className="bg-gray-700 p-4 rounded shadow">
            <p className="text-sm text-gray-400 mb-2">Recent Activity</p>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span>NovaTech</span>{" "}
                <span className="text-green-400">Updated</span>
              </li>
              <li className="flex justify-between">
                <span>Acme Corp</span>{" "}
                <span className="text-blue-400">New Listing</span>
              </li>
              <li className="flex justify-between">
                <span>BetaSoft Ltd</span>{" "}
                <span className="text-yellow-400">Pending Review</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Loader Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-50">
          <Loader message="Logging in..." />
        </div>
      )}
    </div>
  );
}
